/** @jsxImportSource @emotion/react */
import { Box, Button, Heading, Input, Text } from '@landtrustinc/design-system';
import React from 'react';

import type { OtpContext } from '../../../types';
import { useProfileStep } from '../hooks/useProfileStep';
import type { AnalyticsHandler } from '../hooks/useSignupFlow';

type ProfileStepProps = {
  apiBaseUrl: string;
  email: string;
  idpToken: string | null;
  context?: OtpContext;
  onSuccess: (context?: OtpContext) => void;
  onSessionExpired: () => void;
  onAnalyticsEvent?: AnalyticsHandler;
};

export const ProfileStep: React.FC<ProfileStepProps> = ({
  apiBaseUrl,
  email,
  idpToken,
  context,
  onSuccess,
  onSessionExpired,
  onAnalyticsEvent,
}) => {
  const {
    firstName,
    setFirstName,
    lastName,
    setLastName,
    firstNameError,
    lastNameError,
    signingUp,
    handleCompleteSignup,
  } = useProfileStep({
    apiBaseUrl,
    email,
    idpToken,
    context,
    onSuccess,
    onSessionExpired,
    onAnalyticsEvent,
  });

  return (
    <Box display="flex" flexDirection="column" gap="var(--spacing-3)">
      <Heading size="xs" fontWeight="extrabold" textAlign="center" textWrap="wrap">
        Add a couple more details
      </Heading>

      <Box mb="var(--spacing-3)" mx="var(--spacing-5)">
        <Text size="md" textAlign="center">
          Add your first and last name so we know who you are when using LandTrust.
        </Text>
      </Box>

      <Input
        label="First Name"
        placeholder="First Name"
        value={firstName}
        onChange={(e) => setFirstName(e.target.value)}
        error={firstNameError}
        autoComplete="given-name"
        onKeyDown={(e) => {
          if (e.key === 'Enter' && firstName && lastName && !signingUp) {
            handleCompleteSignup();
          }
        }}
      />

      <Input
        label="Last Name"
        placeholder="Last Name"
        value={lastName}
        onChange={(e) => setLastName(e.target.value)}
        error={lastNameError}
        autoComplete="family-name"
        onKeyDown={(e) => {
          if (e.key === 'Enter' && firstName && lastName && !signingUp) {
            handleCompleteSignup();
          }
        }}
      />

      <Button
        variant="primary"
        fullWidth
        onClick={handleCompleteSignup}
        disabled={!firstName || !lastName || signingUp}
        isLoading={signingUp}
      >
        Continue
      </Button>
    </Box>
  );
};
